import React, { useEffect, useState, useCallback } from "react";
import { contactSupportApi } from "../../services/api";
import { PageHeader, Button, Toggle } from "../../components/ui";
import toast from "react-hot-toast";
import {
  Save,
  RotateCcw,
  ExternalLink,
  CheckCircle,
  XCircle,
  Share2,
  Youtube,
  Twitter,
  Instagram,
  Facebook,
  Linkedin,
  Globe,
  Send,          // Telegram
  Music2,        // TikTok stand-in
  AlertCircle,
} from "lucide-react";

// ── Platform config ───────────────────────────────────────────────────────────
// Maps SocialPlatformType enum values → display metadata
const PLATFORM_CONFIG = {
  FACEBOOK:   { label: "Facebook",   icon: Facebook,  color: "#1877F2", placeholder: "https://facebook.com/yourpage"    },
  INSTAGRAM:  { label: "Instagram",  icon: Instagram, color: "#E1306C", placeholder: "https://instagram.com/yourhandle" },
  TWITTER:    { label: "Twitter / X", icon: Twitter,  color: "#1DA1F2", placeholder: "https://twitter.com/yourhandle"   },
  YOUTUBE:    { label: "YouTube",    icon: Youtube,   color: "#FF0000", placeholder: "https://youtube.com/@yourchannel" },
  LINKEDIN:   { label: "LinkedIn",   icon: Linkedin,  color: "#0A66C2", placeholder: "https://linkedin.com/company/you" },
  TELEGRAM:   { label: "Telegram",   icon: Send,      color: "#229ED9", placeholder: "https://t.me/yourchannel"         },
  TIKTOK:     { label: "TikTok",     icon: Music2,    color: "#010101", placeholder: "https://tiktok.com/@yourhandle"   },
  WEBSITE:    { label: "Website",    icon: Globe,     color: "#0F3D3E", placeholder: "https://yourwebsite.com"          },
};

// Fallback for unknown types
const DEFAULT_PLATFORM = { label: "Link", icon: Globe, color: "#6b7280", placeholder: "https://..." };

function getPlatform(type) {
  return PLATFORM_CONFIG[type] || { ...DEFAULT_PLATFORM, label: type };
}

// ── Single social link card ───────────────────────────────────────────────────
function SocialLinkCard({ link, onSave, onToggle }) {
  const [url,     setUrl]     = useState(link.url     || "");
  const [order,   setOrder]   = useState(link.sortOrder ?? 0);
  const [saving,  setSaving]  = useState(false);
  const [toggling,setToggling]= useState(false);
  const [dirty,   setDirty]   = useState(false);

  const platform = getPlatform(link.type);
  const PlatformIcon = platform.icon;

  // keep local state in sync when parent refreshes
  useEffect(() => {
    setUrl(link.url || "");
    setOrder(link.sortOrder ?? 0);
    setDirty(false);
  }, [link]);

  const handleUrlChange = (v)   => { setUrl(v);   setDirty(true); };
  const handleOrderChange = (v) => { setOrder(v); setDirty(true); };

  const handleSave = async () => {
    if (!url.trim()) { toast.error("URL is required"); return; }
    setSaving(true);
    await onSave(link.type, url.trim(), Number(order));
    setSaving(false);
    setDirty(false);
  };

  const handleReset = () => {
    setUrl(link.url || "");
    setOrder(link.sortOrder ?? 0);
    setDirty(false);
  };

  const handleToggle = async (val) => {
    setToggling(true);
    await onToggle(link.id, val);
    setToggling(false);
  };

  const isActive = link.isActive;

  return (
    <div style={{
      background: "var(--bg-card)",
      border: `1px solid ${isActive ? "var(--border)" : "rgba(239,68,68,0.15)"}`,
      borderRadius: 12,
      overflow: "hidden",
      transition: "border-color 0.2s",
    }}>
      {/* Card header */}
      <div style={{
        padding: "12px 16px",
        borderBottom: "1px solid var(--border)",
        display: "flex", alignItems: "center", gap: 10,
        background: isActive ? `${platform.color}0d` : "rgba(239,68,68,0.04)",
      }}>
        {/* Platform icon circle */}
        <div style={{
          width: 36, height: 36, borderRadius: 10, flexShrink: 0,
          background: `${platform.color}18`,
          border: `1px solid ${platform.color}30`,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <PlatformIcon size={17} strokeWidth={1.8} color={platform.color} />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)" }}>
            {platform.label}
          </div>
          <div style={{ fontSize: 10, color: "var(--text-muted)", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.4px" }}>
            Sort order: {link.sortOrder ?? 0}
          </div>
        </div>

        {/* Status chip */}
        <div style={{
          display: "flex", alignItems: "center", gap: 5,
          fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px",
          color: isActive ? "#22c55e" : "#ef4444",
          background: isActive ? "rgba(34,197,94,0.08)" : "rgba(239,68,68,0.08)",
          border: `1px solid ${isActive ? "rgba(34,197,94,0.2)" : "rgba(239,68,68,0.2)"}`,
          borderRadius: 20, padding: "3px 10px",
        }}>
          {isActive
            ? <CheckCircle size={10} strokeWidth={2.5} />
            : <XCircle     size={10} strokeWidth={2.5} />
          }
          {isActive ? "Active" : "Inactive"}
        </div>

        <Toggle value={!!isActive} onChange={handleToggle} disabled={toggling} />
      </div>

      {/* Card body */}
      <div style={{ padding: "14px 16px", display: "flex", flexDirection: "column", gap: 12 }}>
        {/* URL input */}
        <div>
          <label style={{
            fontSize: 10, fontWeight: 700, color: "var(--text-muted)",
            textTransform: "uppercase", letterSpacing: "0.6px",
            display: "block", marginBottom: 6,
          }}>
            URL
          </label>
          <div style={{ position: "relative" }}>
            <ExternalLink
              size={13} strokeWidth={2}
              style={{
                position: "absolute", left: 10, top: "50%",
                transform: "translateY(-50%)",
                color: dirty ? platform.color : "var(--text-muted)",
                pointerEvents: "none", transition: "color 0.2s",
              }}
            />
            <input
              type="url"
              value={url}
              onChange={e => handleUrlChange(e.target.value)}
              placeholder={platform.placeholder}
              style={{
                width: "100%", paddingLeft: 32, paddingRight: 12,
                paddingTop: 8, paddingBottom: 8,
                borderRadius: 8, border: `1px solid ${dirty ? platform.color + "60" : "var(--border)"}`,
                background: "var(--bg-input)", color: "var(--text-primary)",
                fontSize: 13, fontFamily: "var(--font-body)", outline: "none",
                transition: "border-color 0.2s", boxSizing: "border-box",
              }}
              onFocus={e  => e.target.style.borderColor = platform.color + "80"}
              onBlur={e   => e.target.style.borderColor = dirty ? platform.color + "60" : "var(--border)"}
            />
          </div>
        </div>

        {/* Sort order */}
        <div style={{ display: "flex", alignItems: "flex-end", gap: 12 }}>
          {/* <div style={{ width: 120 }}>
            <label style={{
              fontSize: 10, fontWeight: 700, color: "var(--text-muted)",
              textTransform: "uppercase", letterSpacing: "0.6px",
              display: "block", marginBottom: 6,
            }}>
              Sort Order
            </label>
            <input
              type="number"
              min={0}
              value={order}
              onChange={e => handleOrderChange(e.target.value)}
              style={{
                width: "100%", padding: "8px 10px",
                borderRadius: 8, border: "1px solid var(--border)",
                background: "var(--bg-input)", color: "var(--text-primary)",
                fontSize: 13, fontFamily: "var(--font-body)", outline: "none",
                boxSizing: "border-box",
              }}
              onFocus={e => e.target.style.borderColor = "#0F3D3E"}
              onBlur={e  => e.target.style.borderColor = "var(--border)"}
            />
          </div> */}

          {/* Action buttons */}
          <div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
            {dirty && (
              <button
                type="button"
                onClick={handleReset}
                style={{
                  padding: "7px 12px", borderRadius: 8,
                  border: "1px solid var(--border)", background: "var(--bg-card)",
                  color: "var(--text-secondary)", fontSize: 12, cursor: "pointer",
                  display: "inline-flex", alignItems: "center", gap: 5,
                }}
              >
                <RotateCcw size={12} strokeWidth={2.5} /> Reset
              </button>
            )}
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !dirty}
              style={{
                padding: "7px 14px", borderRadius: 8,
                border: "none",
                background: dirty ? "#0F3D3E" : "var(--border)",
                color: dirty ? "#fff" : "var(--text-muted)",
                fontSize: 12, fontWeight: 600,
                cursor: (saving || !dirty) ? "not-allowed" : "pointer",
                display: "inline-flex", alignItems: "center", gap: 5,
                opacity: saving ? 0.7 : 1,
                transition: "all 0.2s",
              }}
            >
              <Save size={12} strokeWidth={2.5} />
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Add new platform modal ────────────────────────────────────────────────────
function AddPlatformModal({ existing, onAdd, onClose }) {
  const [type,  setType]  = useState("");
  const [url,   setUrl]   = useState("");
  const [order, setOrder] = useState(0);
  const [saving,setSaving]= useState(false);

  const available = Object.keys(PLATFORM_CONFIG).filter(
    k => !existing.includes(k)
  );

  const platform = type ? getPlatform(type) : null;

  const handleAdd = async () => {
    if (!type)       { toast.error("Select a platform");  return; }
    if (!url.trim()) { toast.error("URL is required");     return; }
    setSaving(true);
    await onAdd(type, url.trim(), Number(order));
    setSaving(false);
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(0,0,0,0.45)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "var(--bg-card)", border: "1px solid var(--border)",
          borderRadius: 16, width: 440, maxWidth: "calc(100vw - 32px)",
          boxShadow: "0 24px 64px rgba(0,0,0,0.3)",
        }}
      >
        {/* Header */}
        <div style={{
          padding: "14px 18px", borderBottom: "1px solid var(--border)",
          display: "flex", alignItems: "center", gap: 10,
          background: "rgba(15,61,62,0.05)", borderRadius: "16px 16px 0 0",
        }}>
          <Share2 size={16} strokeWidth={2} color="#0F3D3E" />
          <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)" }}>
            Add Social Link
          </span>
        </div>

        <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 14 }}>
          {/* Platform picker */}
          <div>
            <label style={{
              fontSize: 10, fontWeight: 700, color: "var(--text-muted)",
              textTransform: "uppercase", letterSpacing: "0.6px",
              display: "block", marginBottom: 8,
            }}>
              Platform
            </label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {available.length === 0 && (
                <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                  All platforms have been added.
                </span>
              )}
              {available.map(k => {
                const p = getPlatform(k);
                const PIcon = p.icon;
                const isSelected = type === k;
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setType(k)}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 6,
                      padding: "6px 12px", borderRadius: 20,
                      border: isSelected ? `1.5px solid ${p.color}` : "1px solid var(--border)",
                      background: isSelected ? `${p.color}12` : "var(--bg-input)",
                      color: isSelected ? p.color : "var(--text-secondary)",
                      fontSize: 12, fontWeight: isSelected ? 700 : 500,
                      cursor: "pointer", transition: "all 0.15s",
                    }}
                  >
                    <PIcon size={13} strokeWidth={1.8} />
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* URL */}
          <div>
            <label style={{
              fontSize: 10, fontWeight: 700, color: "var(--text-muted)",
              textTransform: "uppercase", letterSpacing: "0.6px",
              display: "block", marginBottom: 6,
            }}>
              URL
            </label>
            <input
              type="url"
              value={url}
              onChange={e => setUrl(e.target.value)}
              placeholder={platform?.placeholder || "https://..."}
              style={{
                width: "100%", padding: "8px 12px",
                borderRadius: 8, border: "1px solid var(--border)",
                background: "var(--bg-input)", color: "var(--text-primary)",
                fontSize: 13, fontFamily: "var(--font-body)", outline: "none",
                boxSizing: "border-box",
              }}
              onFocus={e => e.target.style.borderColor = "#0F3D3E"}
              onBlur={e  => e.target.style.borderColor = "var(--border)"}
            />
          </div>

          {/* Sort order */}
          <div style={{ width: 120 }}>
            <label style={{
              fontSize: 10, fontWeight: 700, color: "var(--text-muted)",
              textTransform: "uppercase", letterSpacing: "0.6px",
              display: "block", marginBottom: 6,
            }}>
              Sort Order
            </label>
            <input
              type="number" min={0}
              value={order}
              onChange={e => setOrder(e.target.value)}
              style={{
                width: "100%", padding: "8px 10px",
                borderRadius: 8, border: "1px solid var(--border)",
                background: "var(--bg-input)", color: "var(--text-primary)",
                fontSize: 13, fontFamily: "var(--font-body)", outline: "none",
                boxSizing: "border-box",
              }}
              onFocus={e => e.target.style.borderColor = "#0F3D3E"}
              onBlur={e  => e.target.style.borderColor = "var(--border)"}
            />
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: "12px 18px", borderTop: "1px solid var(--border)",
          display: "flex", justifyContent: "flex-end", gap: 8,
        }}>
          <button
            type="button" onClick={onClose}
            style={{
              padding: "7px 16px", borderRadius: 8,
              border: "1px solid var(--border)", background: "var(--bg-card)",
              color: "var(--text-secondary)", fontSize: 12, cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            type="button" onClick={handleAdd}
            disabled={saving || !type || !url.trim()}
            style={{
              padding: "7px 16px", borderRadius: 8,
              border: "none", background: "#0F3D3E", color: "#fff",
              fontSize: 12, fontWeight: 600,
              cursor: (saving || !type || !url.trim()) ? "not-allowed" : "pointer",
              opacity: (saving || !type || !url.trim()) ? 0.55 : 1,
              display: "inline-flex", alignItems: "center", gap: 5,
            }}
          >
            <Save size={12} strokeWidth={2.5} />
            {saving ? "Adding…" : "Add"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function SocialLinks() {
  const [links,   setLinks]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await contactSupportApi.getSocialLinks();
      setLinks(r.data.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load social links");
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (type, url, sortOrder) => {
    try {
      await contactSupportApi.upsertSocialLink({ type, url, sortOrder });
      toast.success("Social link saved");
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save");
    }
  };

  const handleToggle = async (id, active) => {
    try {
      await contactSupportApi.toggleSocialLink(id, active);
      setLinks(prev =>
        prev.map(l => l.id === id ? { ...l, isActive: active } : l)
      );
      toast.success(active ? "Link activated" : "Link deactivated");
    } catch {
      toast.error("Failed to toggle");
    }
  };

  const handleAdd = async (type, url, sortOrder) => {
    try {
      await contactSupportApi.upsertSocialLink({ type, url, sortOrder });
      toast.success("Social link added");
      setShowAdd(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to add");
    }
  };

  const existingTypes = links.map(l => l.type);
  const activeCount   = links.filter(l => l.isActive).length;

  // ── Skeleton ────────────────────────────────────────────────────────────
  if (loading) return (
    <div>
      <style>{`@keyframes shimmer{0%{background-position:-200% 0}100%{background-position:200% 0}}`}</style>
      <PageHeader title="Social Links" subtitle="Manage social platform links shown in the app" />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: 14 }}>
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} style={{
            height: 160, borderRadius: 12,
            background: "linear-gradient(90deg,var(--border) 25%,rgba(15,61,62,0.05) 50%,var(--border) 75%)",
            backgroundSize: "200% 100%", animation: "shimmer 1.5s ease-in-out infinite",
          }} />
        ))}
      </div>
    </div>
  );

  return (
    <div>
      <PageHeader
        title="Social Links"
        subtitle="Manage social platform links displayed in the app"
      />

      {/* Summary bar */}
      <div style={{
        display: "flex", alignItems: "center", gap: 12,
        marginBottom: 20, flexWrap: "wrap",
      }}>
        {/* Stats */}
        <div style={{
          display: "flex", gap: 10, flex: 1, flexWrap: "wrap",
        }}>
          <div style={{
            padding: "8px 14px", borderRadius: 20,
            background: "rgba(15,61,62,0.06)",
            border: "1px solid rgba(15,61,62,0.12)",
            fontSize: 12, fontWeight: 600, color: "#0F3D3E",
            display: "flex", alignItems: "center", gap: 6,
          }}>
            <Share2 size={13} strokeWidth={2} />
            {links.length} platform{links.length !== 1 ? "s" : ""}
          </div>
          <div style={{
            padding: "8px 14px", borderRadius: 20,
            background: "rgba(34,197,94,0.07)",
            border: "1px solid rgba(34,197,94,0.18)",
            fontSize: 12, fontWeight: 600, color: "#16a34a",
            display: "flex", alignItems: "center", gap: 6,
          }}>
            <CheckCircle size={13} strokeWidth={2} />
            {activeCount} active
          </div>
          {links.length - activeCount > 0 && (
            <div style={{
              padding: "8px 14px", borderRadius: 20,
              background: "rgba(239,68,68,0.06)",
              border: "1px solid rgba(239,68,68,0.15)",
              fontSize: 12, fontWeight: 600, color: "#dc2626",
              display: "flex", alignItems: "center", gap: 6,
            }}>
              <XCircle size={13} strokeWidth={2} />
              {links.length - activeCount} inactive
            </div>
          )}
        </div>

        {/* Add button */}
        {/* {Object.keys(PLATFORM_CONFIG).length > existingTypes.length && (
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            style={{
              padding: "8px 16px", borderRadius: 8,
              border: "none", background: "#0F3D3E", color: "#fff",
              fontSize: 12, fontWeight: 600, cursor: "pointer",
              display: "inline-flex", alignItems: "center", gap: 6,
            }}
          >
            <Share2 size={13} strokeWidth={2.5} /> Add Platform
          </button>
        )} */}
      </div>

      {/* Empty state */}
      {links.length === 0 && (
        <div style={{
          textAlign: "center", padding: "60px 20px",
          background: "var(--bg-card)", border: "1px dashed var(--border)",
          borderRadius: 12,
        }}>
          <Share2 size={40} strokeWidth={1} color="var(--text-muted)" style={{ marginBottom: 12 }} />
          <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text-primary)", marginBottom: 6 }}>
            No social links yet
          </div>
          <div style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 16 }}>
            Add your first social platform link to show in the app.
          </div>
          <button
            type="button" onClick={() => setShowAdd(true)}
            style={{
              padding: "8px 18px", borderRadius: 8,
              border: "none", background: "#0F3D3E", color: "#fff",
              fontSize: 12, fontWeight: 600, cursor: "pointer",
            }}
          >
            Add Platform
          </button>
        </div>
      )}

      {/* Cards grid */}
      {links.length > 0 && (
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
          gap: 14,
        }}>
          {links.map(link => (
            <SocialLinkCard
              key={link.id}
              link={link}
              onSave={handleSave}
              onToggle={handleToggle}
            />
          ))}
        </div>
      )}

      {/* Info note */}
      {/* <div style={{
        marginTop: 20, padding: "10px 14px", borderRadius: 8,
        background: "rgba(15,61,62,0.05)", border: "1px solid rgba(15,61,62,0.12)",
        fontSize: 12, color: "var(--text-muted)",
        display: "flex", gap: 8, alignItems: "flex-start",
      }}>
        <AlertCircle size={14} strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }} />
        <span>
          Each platform can have only one link. Sort order controls the display sequence in the app.
          Changes take effect immediately when active.
        </span>
      </div> */}

      {/* Add modal */}
      {showAdd && (
        <AddPlatformModal
          existing={existingTypes}
          onAdd={handleAdd}
          onClose={() => setShowAdd(false)}
        />
      )}
    </div>
  );
}